import type { OfferCurrency } from '../hooks/useExchangeRate'
import { computeFeeSubtotal, type FeeUnit } from '../constants/feeUnits'
import type { CargoMetrics } from './cargoMetrics'

export type TotalsByCurrency = Partial<Record<OfferCurrency, number>>

interface BreakdownItemLike {
  value: number
  currency?: OfferCurrency | string | null
  unit?: FeeUnit | null
}

const CURRENCY_ORDER: OfferCurrency[] = ['USD', 'BRL', 'EUR', 'GBP', 'CNY']

/**
 * Sum breakdown items per currency.
 * If `cargo` is provided, each item's contribution is rate × billableQty(unit, cargo).
 * Without cargo (read-only display), the raw value field is summed as-is — this
 * matches legacy offers that pre-date the unit field.
 */
export function computeTotalsByCurrency(
  breakdown: BreakdownItemLike[],
  fallbackCurrency: OfferCurrency = 'USD',
  cargo?: CargoMetrics
): TotalsByCurrency {
  const totals: TotalsByCurrency = {}
  for (const item of breakdown) {
    if (!item.value || item.value <= 0) continue
    const currency = (item.currency || fallbackCurrency) as OfferCurrency
    const contribution = cargo
      ? computeFeeSubtotal(item.value, item.unit ?? undefined, cargo)
      : item.value
    totals[currency] = (totals[currency] ?? 0) + contribution
  }
  return totals
}

export function totalsByCurrencyEntries(
  totals: TotalsByCurrency | null | undefined
): Array<[OfferCurrency, number]> {
  if (!totals) return []
  return CURRENCY_ORDER
    .filter((c) => totals[c] !== undefined && totals[c]! > 0)
    .map((c) => [c, totals[c] as number])
}

/**
 * Fallback for legacy offers without totals_by_currency.
 * Returns totals derived from total_value + currency, or empty if missing.
 */
export function totalsFromLegacyOffer(offer: {
  totalsByCurrency?: TotalsByCurrency | null
  totalValue?: number | null
  currency?: string | null
}): TotalsByCurrency {
  if (offer.totalsByCurrency && Object.keys(offer.totalsByCurrency).length > 0) {
    return offer.totalsByCurrency
  }
  if (offer.totalValue && offer.currency) {
    return { [offer.currency as OfferCurrency]: offer.totalValue }
  }
  return {}
}
